/**
 * 63.4's future, partial destruction step 3 (ARCHITECTURE §5.13): a tree's forms in play, and the pieces they shed.
 *
 * With `GameConfig.partialDestruction` on, a normal kit tree's form follows its health (constants/damage.ts
 * PROPS.flora.forms): its wood and its crown are the form's (constants/treeFormShapes.ts, lane E's damaged models
 * measured). A blow that steps a tree's form sheds the limbs the step lost: each falls by a keyed closed-form rule — no
 * integration — out from the trunk and away from the blow, and lies on the ground as a `DebrisState`. Landing on someone
 * is a light `drop` blow (decision 141; the door, core/damage.ts, applies it). Past DEBRIS.cap pieces a planet the oldest
 * settle into heaps. Pure: state in, new values out; no randomness beyond hashes of the debris id.
 */
import { DEBRIS, PROPS, NORMAL_TREE_PERSONS, TREE_SINK_FRACTION } from '../constants';
import { TREE_WOOD, type WoodPiece } from '../constants/treeWood';
import { TREE_FORM_SHAPES, type TreeFormShape } from '../constants/treeFormShapes';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { getSurfaceRadius } from './surface';
import { seatRadius } from './propSeat';
import { lyingRest, restPoint, type LyingRest } from './lyingRest';
import { personSurfaceRadius, type GroundDials } from './fineTerrain';
import { drawnPieces } from './treeShape';
import { treeFrame, treeFallYaw, treeVariant, treeYaw, propHash } from './treeDraws';
import { dpow } from './dmath';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import type { DebrisState, FloraState, GameState, PlanetState, Vec3 } from '../types';
import type { ObstaclePiece } from './surfaceWalk';

/**
 * The form a prop of `kind` at `health` takes — the index of the deepest of its table's forms whose share it is at or
 * under, or -1 whole (no forms, no health, or above them all).
 */
export function propForm(kind: 'flora' | 'rock', health: number): number {
  const def = PROPS[kind];
  if (def.health === null || !def.forms) return -1;
  let form = -1;
  def.forms.forEach((f, i) => { if (health <= f.at * def.health!) form = i; });
  return form;
}

/** A tree's form in play: -1 (whole) unless the switch is on and it is a normal tree whose species has forms. */
export function treeForm(state: Partial<Pick<GameState, 'config' | 'floraHealth'>>, f: FloraState): number {
  if (!state.config?.partialDestruction || !f.alive || f.size !== 'normal' || !TREE_FORM_SHAPES[f.floraType]) return -1;
  return propForm('flora', state.floraHealth?.[f.id] ?? PROPS.flora.health!);
}

/** A form's measured wood and crown for a tree (its drawn variant), or null when it has none. */
export function treeFormShape(f: FloraState, form: number): TreeFormShape | null {
  return form < 0 ? null : TREE_FORM_SHAPES[f.floraType]?.variants[treeVariant(f.id, f.floraType)]?.[form] ?? null;
}

const formsKeys = new WeakMap<object, string>();
/**
 * The trees' forms as one string (the damaged trees' `id:form`, those past whole), memoised on the health record — what
 * a planet's shape memos key on with the switch on, so a blow that steps no form rebuilds nothing. Null with it off.
 */
export function formsKey(state: Partial<Pick<GameState, 'config' | 'floraHealth'>>): string | null {
  if (!state.config?.partialDestruction) return null;
  const h = state.floraHealth;
  if (!h) return '';
  let key = formsKeys.get(h);
  if (key === undefined) {
    const ids = Object.keys(h).map(Number).sort((a, b) => a - b);
    key = ids.map(id => { const form = propForm('flora', h[id]); return form >= 0 ? `${id}:${form}` : ''; }).filter(Boolean).join(',');
    formsKeys.set(h, key);
  }
  return key;
}

/** World units per model unit for a normal tree (as its walk obstacle and its crown are sized). */
function treeK(state: Pick<GameState, 'config'>, f: FloraState): number {
  return f.scale * NORMAL_TREE_PERSONS * PERSON_BODY_HEIGHT * personSetOf({ config: state.config }).k;
}

/** Model wood pieces in the world, as offsets from the tree's foot (core/surfaceWalk.ts treePieces' rule). */
function woodOffsets(pieces: readonly WoodPiece[], up: Vec3, yaw: number, k: number, height: number): ObstaclePiece[] {
  const toWorld = treeFrame(up, yaw), sink = TREE_SINK_FRACTION * height;
  return pieces.map(p => ({ a: toWorld(p[0] * k, (p[1] - sink) * k, p[2] * k), b: toWorld(p[4] * k, (p[5] - sink) * k, p[6] * k), ra: p[3] * k, rb: p[7] * k }));
}

/** A frustum's volume (world units³). */
const frustumVolume = (length: number, ra: number, rb: number) => (Math.PI / 3) * length * (ra * ra + ra * rb + rb * rb);
/** A piece's or a heap's volume. */
export function debrisVolume(d: DebrisState): number { return d.heap ?? frustumVolume(d.length, d.ra, d.rb); }
/** A heap's radius from its volume: a mound DEBRIS.heapAspect as tall as it is wide in radius, narrowing to half on top. */
export function heapRadius(volume: number): number { return dpow((3 * volume) / (1.75 * Math.PI * DEBRIS.heapAspect), 1 / 3); }

/**
 * The limbs a tree loses stepping from form `from` to form `to` (-1 = whole), fallen: each piece of its wood that the
 * step shortened by more than DEBRIS.minLength people loses the part past its new end. The part falls from where it was
 * — out from the trunk and away from the blow (`blowFrom`, world; a hashed way when absent), a keyed share of its height
 * (DEBRIS.reach) — and lies along a keyed way. Ids from `seq` on.
 */
export function shedLimbs(state: Pick<GameState, 'config' | 'planets'>, f: FloraState, from: number, to: number, blowFrom: Vec3 | undefined, tick: number, seq: number): DebrisState[] {
  const wood = TREE_WOOD[f.floraType]?.variants[treeVariant(f.id, f.floraType)];
  const before = from < 0 ? wood?.whole : treeFormShape(f, from)?.wood, after = treeFormShape(f, to)?.wood;
  const planet = state.planets[f.planetIndex];
  if (!wood || !before || !after || !planet || to <= from) return [];
  // (71.2: from the tree's foot where it stands — on the fine ground at its trunk, core/propSeat.ts)
  const up = vecNormalize(f.surfaceDir), foot = vecAdd(planet.position, vecScale(up, seatRadius(planet, { dir: f.surfaceDir, half: 0 }, up, personSetOf({ config: state.config }))));
  const k = treeK(state, f), yaw = treeYaw(f.id);
  // (403: the limbs as the tree drew them — its own shape before and after the step)
  const was = woodOffsets(drawnPieces(f, wood, before), up, yaw, k, wood.height), now = woodOffsets(drawnPieces(f, wood, after), up, yaw, k, wood.height);
  const person = PERSON_BODY_HEIGHT * personSetOf({ config: state.config }).k;
  const out: DebrisState[] = [];
  for (let i = 0; i < was.length; i++) {
    const w = was[i], n = now[i];
    // the lost part: past the shortened piece's end (or the whole piece, when the form has none in its place)
    const a = n ? n.b : w.a, ra = n ? n.rb : w.ra;
    const length = vecLength(vecSub(w.b, a));
    if (!(length > DEBRIS.minLength * person)) continue;
    const id = seq + out.length;
    const mid = vecScale(vecAdd(a, w.b), 0.5), height = Math.max(0, vecDot(mid, up));
    const across = vecSub(mid, vecScale(up, vecDot(mid, up)));
    const away = treeFrame(up, treeFallYaw(id, up, foot, blowFrom))(0, 0, 1);
    const reach = (DEBRIS.reach[0] + (DEBRIS.reach[1] - DEBRIS.reach[0]) * propHash(id, 31)) * height;
    const land = vecAdd(foot, vecAdd(across, vecScale(away, reach)));
    out.push({ id, planetIndex: f.planetIndex, dir: vecNormalize(vecSub(land, planet.position)), yaw: propHash(id, 32) * Math.PI * 2, length, ra, rb: w.rb, tick });
  }
  return out;
}

/** Where a piece lies: its foot on the ground (world) and its up — 71.2: on the fine ground, sunk by its material (core/propSeat.ts), as its walk obstacle's seat (and its drawing's, rendering/debrisView.ts). */
export function debrisFoot(planet: PlanetState, d: DebrisState, ps: GroundDials | null): { foot: Vec3; up: Vec3 } {
  const up = vecNormalize(d.dir);
  return { foot: vecAdd(planet.position, vecScale(up, seatRadius(planet, { dir: d.dir, half: 0 }, up, ps))), up };
}

/**
 * A piece's or a heap's wood as offsets from its foot: a lying piece along its way, its axis at its thicker radius over
 * the ground; a heap as a mound (a frustum narrowing to half its radius).
 */
export function debrisPieces(d: DebrisState, up: Vec3, rest?: LyingRest | null): ObstaclePiece[] {
  if (d.heap !== undefined) {
    const r = heapRadius(d.heap);
    return [{ a: { x: 0, y: 0, z: 0 }, b: vecScale(up, r * DEBRIS.heapAspect), ra: r, rb: r / 2 }];
  }
  const at = treeFrame(up, d.yaw), lift = Math.max(d.ra, d.rb);
  // (70.4: resting along the ground from its middle, as debrisRest finds it)
  if (rest) return [{ a: restPoint(at, rest, 0, lift, -d.length / 2), b: restPoint(at, rest, 0, lift, d.length / 2), ra: d.ra, rb: d.rb }];
  return [{ a: at(0, lift, -d.length / 2), b: at(0, lift, d.length / 2), ra: d.ra, rb: d.rb }];
}

/** 70.4: how a lying piece rests on the fine ground along its length from its foot (core/lyingRest.ts); null for a heap or without the fine ground (flat, as before). */
export function debrisRest(planet: PlanetState, d: DebrisState, ps: GroundDials | null | undefined): LyingRest | null {
  if (d.heap !== undefined || !ps || !(ps.fineAmplitude > 0)) return null;
  const { foot, up } = debrisFoot(planet, d, ps);
  return lyingRest(planet, foot, treeFrame(up, d.yaw)(0, 0, 1), -d.length / 2, d.length / 2, ps);
}

/** The pieces' reach about their foot and their top (world units): a walk obstacle's radius and height. */
export function debrisExtent(d: DebrisState): { radius: number; height: number } {
  if (d.heap !== undefined) { const r = heapRadius(d.heap); return { radius: r, height: r * DEBRIS.heapAspect }; }
  const r = Math.max(d.ra, d.rb);
  return { radius: d.length / 2 + r, height: 2 * r };
}

/**
 * The debris with each planet at most DEBRIS.cap: past it, the oldest lying piece settles with its nearest neighbour on
 * its planet outside the newest half of the cap (a limb that just fell lies as it fell) — into it when that is a heap,
 * else the two become one heap where the neighbour lies — until the planet is under the cap; with no lying piece left the
 * oldest heap joins its nearest heap. Ties by id.
 */
export function settleDebris(list: readonly DebrisState[], planets: readonly PlanetState[]): DebrisState[] {
  let out = [...list];
  const planetsOf = [...new Set(out.map(d => d.planetIndex))];
  for (const pi of planetsOf) {
    const planet = planets[pi];
    if (!planet) continue;
    for (;;) {
      const here = out.filter(d => d.planetIndex === pi);
      if (here.length <= DEBRIS.cap) break;
      const byAge = (a: DebrisState, b: DebrisState) => a.tick - b.tick || a.id - b.id;
      const lying = here.filter(d => d.heap === undefined).sort(byAge);
      const go = lying[0] ?? here.filter(d => d.heap !== undefined).sort(byAge)[0];
      const fresh = new Set(lying.slice(-Math.floor(DEBRIS.cap / 2)));
      let near: DebrisState | null = null, best = Infinity;
      for (const d of here) {
        if (d === go || fresh.has(d) || (lying.length === 0 && d.heap === undefined)) continue;
        const dist = vecLength(vecSub(d.dir, go.dir));
        if (dist < best || (dist === best && near && d.id < near.id)) { best = dist; near = d; }
      }
      if (!near) break;
      const merged: DebrisState = { ...near, heap: debrisVolume(near) + debrisVolume(go), length: 0, ra: 0, rb: 0 };
      out = out.filter(d => d !== go).map(d => d === near ? merged : d);
    }
  }
  return out;
}

/**
 * Who a lying piece lands on and the blow (decision 141): the living units whose middle is within the piece's radius
 * plus their own of its length on the ground — DEBRIS.hitPerVolume a person³ of its wood, at most DEBRIS.hitMax, and
 * never the last of anyone's health (a small piece is a reason to keep clear, never a kill).
 */
export function debrisLandsOn(state: Pick<GameState, 'config' | 'planets' | 'players'>, d: DebrisState): Array<{ unitId: number; amount: number }> {
  const planet = state.planets[d.planetIndex];
  if (!planet || d.heap !== undefined) return [];
  const ps = personSetOf({ config: state.config }), person = PERSON_BODY_HEIGHT * ps.k;
  const { foot, up } = debrisFoot(planet, d, ps);
  const [p] = debrisPieces(d, up, debrisRest(planet, d, ps));
  const a = vecAdd(foot, p.a), b = vecAdd(foot, p.b), ab = vecSub(b, a), ll = vecDot(ab, ab);
  const blow = Math.min(DEBRIS.hitMax, DEBRIS.hitPerVolume * debrisVolume(d) / (person * person * person));
  const out: Array<{ unitId: number; amount: number }> = [];
  for (const u of state.players) {
    if (!u.alive || u.planetIndex !== d.planetIndex) continue;
    const un = vecNormalize(u.surfaceDir);
    const mid = vecAdd(planet.position, vecScale(un, (ps.fineAmplitude > 0 ? personSurfaceRadius(planet, un, ps) : getSurfaceRadius(planet, un)) + person / 2));   // (71.2: a unit's middle over the ground it stands on)
    const t = ll > 0 ? Math.max(0, Math.min(1, vecDot(vecSub(mid, a), ab) / ll)) : 0;
    const gap = vecLength(vecSub(mid, vecAdd(a, vecScale(ab, t))));
    const amount = Math.min(blow, u.health - 1);
    if (gap <= Math.max(d.ra, d.rb) + ps.unitRadius + person / 2 && amount > 0) out.push({ unitId: u.id, amount });
  }
  return out;
}
